import { useEffect } from 'react';

import { Group, Stack, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';

import { IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Select } from '../../../shared/components/UI/Select/Select';
import { SidepanelHeader } from '../../../shared/components/UI/Sidepanel/Sidepanel';
import { TextInput } from '../../../shared/components/UI/TextInput/TextInput';
import { Tooltip } from '../../../shared/components/UI/Tooltip/Tooltip';

import { MutationFeedback } from '../../../shared/components/MutationFeedback';
import type { Role } from '../../../shared/entities/Access';
import type { WorkspaceUser } from '../../../shared/entities/User';
import { useUpdateWorkspaceUser } from '../hooks/useUpdateWorkspaceUser.hook';
import { RoleSelect } from './RoleSelect';

type FormValues = {
  firstName: string;
  lastName: string;
  role: Role;
  disabled: string;
};

export function WorkspaceUserSidePanel({
  user,
  closePanel,
}: {
  user: WorkspaceUser;
  closePanel: () => void;
}) {
  const { t } = useTranslation('workspaceSettings');

  const updateWorkspaceUserMutation = useUpdateWorkspaceUser(Number(user.id));

  const form = useForm<FormValues>({
    initialValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      disabled: user.disabledAt ? 'true' : 'false',
    },
    validateInputOnBlur: true,
    validate: {
      firstName: (value: string) =>
        value.length > 0 ? null : t('inputs.firstName.error'),
      lastName: (value: string) =>
        value.length > 0 ? null : t('inputs.lastName.error'),
    },
  });

  const [debouncedFormValues] = useDebouncedValue(form.values, 500);

  useEffect(() => {
    if (form.isTouched() && form.isValid()) {
      updateWorkspaceUserMutation.mutate({
        ...debouncedFormValues,
        disabled: debouncedFormValues.disabled === 'true',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormValues]);

  return (
    <Stack h="100%" spacing="04" w="680px">
      <SidepanelHeader
        header={<Title order={4}>{user.firstName + ' ' + user.lastName}</Title>}
        closePanel={closePanel}
      >
        <MutationFeedback mutations={[updateWorkspaceUserMutation]} />
      </SidepanelHeader>

      <Stack px="05" spacing="04">
        <Stack spacing="02">
          <Group noWrap align="center" spacing="01" c="gray.5">
            <Text size="sm" fw={500} c="gray.9">
              {t('inputs.email.label')}
            </Text>
            <Tooltip content={t('contactTengoToChangeEmail')}>
              <IconInfoCircle size={16} />
            </Tooltip>
          </Group>
          <TextInput
            variant="filled"
            disabled
            sx={{ '&:[data-disabled]': { color: 'red.5' } }}
            value={user.email}
          />
        </Stack>

        <TextInput
          variant="filled"
          label={t('inputs.firstName.label')}
          placeholder={t('inputs.firstName.placeholder')}
          {...form.getInputProps('firstName')}
        />

        <TextInput
          variant="filled"
          label={t('inputs.lastName.label')}
          placeholder={t('inputs.lastName.placeholder')}
          {...form.getInputProps('lastName')}
        />

        <RoleSelect variant="filled" {...form.getInputProps('role')} />

        <Select
          variant="filled"
          label={t('status.label')}
          clearable={false}
          data={[
            { value: 'false', label: t('status.activated') },
            { value: 'true', label: t('status.deactivated') },
          ]}
          {...form.getInputProps('disabled')}
        />
      </Stack>
    </Stack>
  );
}
